// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { mergeWithCss, xScreen } from "@werner/XDL/Utilities";
import React, { ReactNode, useMemo, useState } from "react";
import { css } from "styled-components";

interface ProductGridContainerContextType {
    styles: ProductGridContainerStyle;
}
export interface ProductGridContainerStyle {
    container?: InjectableCss;
    info?: InjectableCss;
    addToList?: InjectableCss;
    price?: InjectableCss;
    availability?: InjectableCss;
    addToCartActions?: InjectableCss;
    image?: InjectableCss;
    priceAvailabilityLink?: InjectableCss;
}

const productGridContainerStyle: ProductGridContainerStyle = {
    container: {
        css: css`
            height: 100%;
            position: relative;
            display: flex;
            flex-direction: column;
            max-width: 224px;
            width: 100%;
            border-radius: 0;
            background-color: transparent;
            box-shadow: none;
            box-sizing: border-box;
            ${xScreen("lte", "sm")} {
                margin: 0 auto;
            }
            ${xScreen("lte", "xs")} {
                max-width: 100%;
            }

            & > .top-section {
                margin-bottom: 16px;
            }
            & > .middle-section {
                margin-bottom: 8px;
            }
            & > .bottom-section {
                margin-top: auto;
                display: flex;
                flex-direction: column;
                gap: 14px;
            }
        `,
    },
    image: {
        css: css`
            position: relative;
            width: 222px;
            height: 222px;
            padding: 30px;
            margin: 0 auto;
        `,
    },
    info: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 10px;
        `,
    },
    price: {
        css: css``,
    },
    availability: {
        css: css``,
    },
    addToCartActions: {
        css: css`
            display: flex;
            margin-top: auto;
        `,
    },
    addToList: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
        `,
    },
};

const ProductGridContainerContext = React.createContext<ProductGridContainerContextType>({
    styles: {},
});
const sharedClassName = "product-grid-container";

const enum NAMES {
    Image = "Image",
    Info = "Info",
    AddToCartActions = "AddToCartActions",
    Price = "Price",
    Availability = "Availability",
    AddToList = "AddToList",
}

const getChild = (children: React.ReactElement[], childName: NAMES) =>
    children.find(child => (child.type as React.NamedExoticComponent<any>)?.displayName === childName);

const getDestructeredChildren = (childrenArray: React.ReactElement[]) => {
    return [
        getChild(childrenArray, NAMES.Image),
        getChild(childrenArray, NAMES.Info),
        getChild(childrenArray, NAMES.AddToCartActions),
        getChild(childrenArray, NAMES.Price),
        getChild(childrenArray, NAMES.Availability),
        getChild(childrenArray, NAMES.AddToList),
    ];
};

const ProductGridContainer = ({
    children,
    extendedStyles,
    ...otherProps
}: {
    children: any;
    extendedStyles?: ProductGridContainerStyle;
    [key: string]: any;
}) => {
    const [styles] = useState(() => mergeWithCss(productGridContainerStyle, extendedStyles));
    const value = useMemo(() => ({ styles }), [styles]);
    const childrenArray = React.Children.toArray(children) as React.ReactElement[];
    const [image, info, addToCartActions, price, availability, addToList] = React.useMemo(
        () => getDestructeredChildren(childrenArray),
        [childrenArray],
    );
    return (
        <ProductGridContainerContext.Provider value={value}>
            <StyledWrapper {...styles.container} {...otherProps} className={sharedClassName}>
                <StyledWrapper className="top-section">{image}</StyledWrapper>
                <StyledWrapper className="middle-section">{info}</StyledWrapper>
                <StyledWrapper className="bottom-section">
                    {price}
                    {availability}
                    {addToCartActions}
                    {addToList}
                </StyledWrapper>
            </StyledWrapper>
        </ProductGridContainerContext.Provider>
    );
};

export const useProductGridContainerContext = () => {
    const context = React.useContext(ProductGridContainerContext);
    if (!context) {
        throw new Error("ProductGridTemplate compound components cannot be rendered outside ProductGridContainer");
    }
    return context;
};

const Image: React.FC<{ children: ReactNode }> = ({ children, ...otherProps }) => {
    const {
        styles: { image },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...image} className={`${sharedClassName}-image`}>
            {children}
        </StyledWrapper>
    );
};

const Info: React.FC<{ children: ReactNode }> = ({ children, ...otherProps }) => {
    const {
        styles: { info },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...info} className={`${sharedClassName}-info`}>
            {children}
        </StyledWrapper>
    );
};

const AddToCartActions: React.FC<{ children: ReactNode }> = ({ children, ...otherProps }) => {
    const {
        styles: { addToCartActions },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...addToCartActions} className={`${sharedClassName}-addToCartActions`}>
            {children}
        </StyledWrapper>
    );
};

const AddToList: React.FC<{ children: ReactNode }> = ({ children, ...otherProps }) => {
    const {
        styles: { addToList },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...addToList} className={`${sharedClassName}-addToList`}>
            {children}
        </StyledWrapper>
    );
};

const Price: React.FC<{ children: ReactNode }> = ({ children, ...otherProps }) => {
    const {
        styles: { price },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...price} className={`${sharedClassName}-price`}>
            {children}
        </StyledWrapper>
    );
};

const Availability: React.FC<{ children: ReactNode }> = ({ children, ...otherProps }) => {
    const {
        styles: { availability },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...availability} className={`${sharedClassName}-availability`}>
            {children}
        </StyledWrapper>
    );
};

ProductGridContainer.Image = Image;
ProductGridContainer.Image.displayName = NAMES.Image;
ProductGridContainer.Info = Info;
ProductGridContainer.Info.displayName = NAMES.Info;
ProductGridContainer.AddToCartActions = AddToCartActions;
ProductGridContainer.AddToCartActions.displayName = NAMES.AddToCartActions;
ProductGridContainer.Price = Price;
ProductGridContainer.Price.displayName = NAMES.Price;
ProductGridContainer.Availability = Availability;
ProductGridContainer.Availability.displayName = NAMES.Availability;
ProductGridContainer.AddToList = AddToList;
ProductGridContainer.AddToList.displayName = NAMES.AddToList;

export default ProductGridContainer;
